// src/utils/dataAudit.ts - Auditoria e Normalização de Dados de Pacientes

import { supabase } from '../config/supabase';
import { describeAppointmentLocationIssue } from './appointmentLocation';

export interface PatientDataIssue {
  patientId: string;
  patientName: string;
  missingFields: string[];
  inconsistentFields: string[];
  emptyFields: string[];
  financialStructure: 'complete' | 'partial' | 'missing';
  severity: 'critical' | 'warning' | 'info';
}

export interface AuditReport {
  totalPatients: number;
  patientsWithIssues: number;
  patientsHealthy: number;
  issuesByType: {
    missingFields: number;
    inconsistentFields: number;
    emptyFields: number;
    noFinancialData: number;
  };
  issues: PatientDataIssue[];
  timestamp: string;
  appointmentAudit: AppointmentAuditReport;
}

export interface AppointmentAuditIssue {
  appointmentId: string;
  patientId?: string;
  suggestedPatientId?: string;
  patientName?: string;
  startDate?: string;
  createdAt?: string;
  appointmentLocation?: string | null;
  missingFields: string[];
  severity: 'critical' | 'warning';
}

export interface AppointmentAuditReport {
  totalEvents: number;
  withPatientId: number;
  withoutPatientId: number;
  unknownPatientId: number;
  missingStart: number;
  missingStatus: number;
  missingLocation: number;
  invalidLocation: number;
  issues: AppointmentAuditIssue[];
}

const normalizeText = (value: string | undefined): string => {
  if (!value) return '';
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const convertToDate = (value: any): Date | null => {
  if (!value) return null;
  try {
    if (value?.toDate && typeof value.toDate === 'function') {
      const converted = value.toDate();
      return converted instanceof Date && !Number.isNaN(converted.getTime()) ? converted : null;
    }
    if (value instanceof Date) {
      return !Number.isNaN(value.getTime()) ? value : null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch (err) {
    return null;
  }
};

export interface NormalizationResult {
  success: boolean;
  patientsProcessed: number;
  patientsFixed: number;
  errors: Array<{ patientId: string; error: string }>;
}

/**
 * Realiza auditoria completa dos dados de pacientes
 */
export async function auditPatientData(): Promise<AuditReport> {
  console.log('🔍 Iniciando auditoria de dados de pacientes...');

  const { data: patientsData, error } = await supabase
    .from('patients') // Assumindo que a tabela de pacientes no Supabase se chama 'patients'
    .select('*');

  if (error) {
    console.error('Erro ao buscar pacientes do Supabase:', error);
    throw error;
  }

  const issues: PatientDataIssue[] = [];
  const issuesByType = {
    missingFields: 0,
    inconsistentFields: 0,
    emptyFields: 0,
    noFinancialData: 0,
  };

  const patientIdSet = new Set<string>();
  const nameIndex = new Map<string, string>();

  patientsData.forEach(patientRow => {
    const issue = analyzePatientData(patientRow.patientId, patientRow); // Usando patientId do Supabase

    patientIdSet.add(patientRow.patientId);
    const normalizedName = normalizeText(`${patientRow.patientFullName || ''}`); // Assumindo patientFullName
    if (normalizedName) {
      nameIndex.set(normalizedName, patientRow.patientId);
    }

    if (issue) {
      issues.push(issue);

      // Contar tipos de problemas
      if (issue.missingFields.length > 0) issuesByType.missingFields++;
      if (issue.inconsistentFields.length > 0) issuesByType.inconsistentFields++;
      if (issue.emptyFields.length > 0) issuesByType.emptyFields++;
      if (issue.financialStructure === 'missing') issuesByType.noFinancialData++;
    }
  });

  const report: AuditReport = {
    totalPatients: patientsData.length,
    patientsWithIssues: issues.length,
    patientsHealthy: patientsData.length - issues.length,
    issuesByType,
    issues,
    timestamp: new Date().toISOString(),
    appointmentAudit: await auditAppointmentData(patientIdSet, nameIndex)
  };

  console.log('✅ Auditoria concluída:', {
    total: report.totalPatients,
    comProblemas: report.patientsWithIssues,
    saudaveis: report.patientsHealthy,
  });

  return report;
}

async function auditAppointmentData(
  knownPatientIds: Set<string>,
  normalizedNames: Map<string, string>
): Promise<AppointmentAuditReport> {
  console.log('🔍 Iniciando auditoria dos agendamentos...');

  const { data: appointmentsData, error } = await supabase
    .from('appointments')
    .select('*');

  if (error) {
    console.error('Erro ao buscar agendamentos do Supabase:', error);
    throw error;
  }

  const issues: AppointmentAuditIssue[] = [];

  let withPatientId = 0;
  let withoutPatientId = 0;
  let unknownPatientId = 0;
  let missingStart = 0;
  let missingStatus = 0;
  let missingLocation = 0;
  let invalidLocation = 0;

  appointmentsData.forEach((data: any) => {
    const missing: string[] = [];
    let severity: AppointmentAuditIssue["severity"] = 'warning';

    let resolvedPatientId: string | undefined;
    let suggestedPatientId: string | undefined;
    let resolvedPatientName: string | undefined = data.appointmentTitle || '';

    const hasPatientId = typeof data.patientId === 'string' && data.patientId.trim() !== '';

    if (hasPatientId) {
      withPatientId++;
      const trimmedId = data.patientId.trim();
      if (knownPatientIds.has(trimmedId)) {
        resolvedPatientId = trimmedId;
      } else {
        resolvedPatientId = trimmedId;
        unknownPatientId++;
        missing.push('patientId inválido');
      }
    } else {
      withoutPatientId++;
      missing.push('patientId ausente');
      severity = 'critical';
    }

    if (!resolvedPatientId && resolvedPatientName) {
      const normalizedCandidate = normalizeText(resolvedPatientName);
      if (normalizedCandidate) {
        for (const [normalizedName, patientId] of normalizedNames.entries()) {
          if (
            normalizedCandidate.includes(normalizedName) ||
            normalizedName.includes(normalizedCandidate)
          ) {
            suggestedPatientId = patientId;
            break;
          }
        }
      }
    }

    const startDate = convertToDate(data.appointmentStartDate);
    if (!startDate || Number.isNaN(startDate.getTime())) {
      missingStart++;
      missing.push('appointmentStartDate');
      severity = 'critical';
    }

    const status = typeof data.appointmentStatus === 'string' ? data.appointmentStatus.trim() : '';
    if (!status) {
      missingStatus++;
      missing.push('appointmentStatus');
    }

    const locationIssue = describeAppointmentLocationIssue(data.appointmentLocation);
    if (locationIssue === 'Local ausente') {
      missingLocation++;
      missing.push('appointmentLocation ausente');
      severity = 'critical';
    } else if (locationIssue) {
      invalidLocation++;
      missing.push(`appointmentLocation inválido (${String(data.appointmentLocation).trim()})`);
      if (severity !== 'critical') {
        severity = 'warning';
      }
    }

    const createdAt = convertToDate(data.appointmentCreatedAt);

    if (missing.length > 0) {
      issues.push({
        appointmentId: data.appointmentId,
        patientId: resolvedPatientId,
        suggestedPatientId,
        patientName: resolvedPatientName || undefined,
        startDate: startDate ? startDate.toISOString() : undefined,
        createdAt: createdAt ? createdAt.toISOString() : undefined,
        appointmentLocation: data.appointmentLocation ?? null,
        missingFields: missing,
        severity
      });
    }
  });

  const report: AppointmentAuditReport = {
    totalEvents: appointmentsData.length,
    withPatientId,
    withoutPatientId,
    unknownPatientId,
    missingStart,
    missingStatus,
    missingLocation,
    invalidLocation,
    issues
  };

  console.log('✅ Auditoria dos agendamentos concluída:', report);

  return report;
}

/**
 * Analisa um documento de paciente e identifica problemas
 */
function analyzePatientData(patientId: string, data: any): PatientDataIssue | null {
  const missingFields: string[] = [];
  const inconsistentFields: string[] = [];
  const emptyFields: string[] = [];

  // Verificar nome
  const nome = data.nome || '';
  const sobrenome = data.sobrenome || '';
  const fullName = `${nome} ${sobrenome}`.trim();

  if (!fullName) {
    missingFields.push('nome');
  }

  // Verificar campos de contato
  const hasEmail = !!(data.email || data.emailAddress);
  const hasPhone = !!(data.telefone || data.phone || data.celular);

  if (!hasEmail) {
    missingFields.push('email');
  } else {
    // Verificar inconsistência de nomenclatura
    if (data.email && data.emailAddress) {
      inconsistentFields.push('email (duplicado como email e emailAddress)');
    }
  }

  if (!hasPhone) {
    missingFields.push('telefone');
  } else {
    // Verificar inconsistência de nomenclatura
    const phoneFields = [data.telefone, data.phone, data.celular].filter(Boolean);
    if (phoneFields.length > 1) {
      inconsistentFields.push('telefone (múltiplos campos: telefone/phone/celular)');
    }
  }

  // Verificar CPF
  if (!data.cpf && !data.documento) {
    missingFields.push('cpf');
  } else if (data.cpf && data.documento) {
    inconsistentFields.push('cpf (duplicado como cpf e documento)');
  }

  // Verificar data de nascimento
  if (!data.dataNascimento && !data.data_x0020_de_x0020_nascimento && !data.birthDate) {
    missingFields.push('dataNascimento');
  }

  // Verificar campos vazios mas existentes
  if (data.email === '' || data.emailAddress === '') {
    emptyFields.push('email');
  }
  if (data.telefone === '' || data.phone === '' || data.celular === '') {
    emptyFields.push('telefone');
  }

  // Analisar estrutura financeira
  let financialStructure: 'complete' | 'partial' | 'missing' = 'missing';

  if (data.financeiro) {
    const hasActive = data.financeiro.ativo !== undefined;
    const hasPackage = data.financeiro.temPacote !== undefined;
    const hasPrice = data.financeiro.precoSessaoIndividual !== undefined;
    const hasInvoices = Array.isArray(data.financeiro.faturas);

    if (hasActive && hasPackage && hasPrice && hasInvoices) {
      financialStructure = 'complete';
    } else {
      financialStructure = 'partial';
      missingFields.push('financeiro (estrutura incompleta)');
    }
  } else {
    missingFields.push('financeiro');
  }

  // Determinar severidade
  let severity: 'critical' | 'warning' | 'info' = 'info';

  if (missingFields.includes('nome') || financialStructure === 'missing') {
    severity = 'critical';
  } else if (missingFields.length > 0 || inconsistentFields.length > 0) {
    severity = 'warning';
  }

  // Se não há problemas, retornar null
  if (
    missingFields.length === 0 &&
    inconsistentFields.length === 0 &&
    emptyFields.length === 0 &&
    financialStructure === 'complete'
  ) {
    return null;
  }

  return {
    patientId,
    patientName: fullName || 'Nome não informado',
    missingFields,
    inconsistentFields,
    emptyFields,
    financialStructure,
    severity,
  };
}

/**
 * Normaliza todos os dados de pacientes
 */
export async function normalizeAllPatientData(): Promise<NormalizationResult> {
  console.log('🔧 Iniciando normalização de dados...');

  const { data: patientsData, error } = await supabase
    .from('patients')
    .select('*');

  if (error) {
    console.error('Erro ao buscar pacientes do Supabase para normalização:', error);
    throw error;
  }

  let patientsFixed = 0;
  const errors: Array<{ patientId: string; error: string }> = [];

  for (const patient of patientsData) {
    try {
      const normalizedData = normalizePatientDocument(patient);

      // Se houve mudanças, aplicar
      if (Object.keys(normalizedData).length > 0) {
        const { error: updateError } = await supabase
          .from("patients")
          .update(normalizedData)
          .eq("patientId", patient.patientId);

        if (updateError) {
          throw updateError;
        }
        patientsFixed++;
      }
    } catch (error: any) {
      console.error(`❌ Erro ao normalizar paciente ${patient.patientId}:`, error);
      errors.push({
        patientId: patient.patientId,
        error: error.message,
      });
    }
  }

  console.log('✅ Normalização concluída:', {
    processados: patientsData.length,
    corrigidos: patientsFixed,
    erros: errors.length,
  });

  return {
    success: errors.length === 0,
    patientsProcessed: patientsData.length,
    patientsFixed,
    errors,
  };
}

export async function fixAppointmentIssue(issue: AppointmentAuditIssue): Promise<{ success: boolean; message?: string }> {
  try {
    if (!issue.missingFields.some(field => field.startsWith("patientId")) || !issue.suggestedPatientId || issue.patientId) {
      return {
        success: false,
        message: 'Nenhuma correção automática disponível para este agendamento.'
      };
    }

    const { error: updateError } = await supabase
      .from("appointments")
      .update({ patientId: issue.suggestedPatientId })
      .eq("appointmentId", issue.appointmentId);

    if (updateError) {
      throw updateError;
    }

    console.log(`✅ Agendamento ${issue.appointmentId} atualizado com patientId ${issue.suggestedPatientId}`);

    return { success: true };
  } catch (error: any) {
    console.error(`❌ Erro ao corrigir agendamento ${issue.appointmentId}:`, error);
    return { success: false, message: error.message };
  }
}

/**
 * Normaliza um documento de paciente
 * Retorna apenas os campos que precisam ser atualizados
 */
function normalizePatientDocument(data: any): any {
  const updates: any = {};

  // 1. Consolidar campos de email
  if (data.emailAddress && !data.email) {
    updates.email = data.emailAddress;
    updates.emailAddress = null; // Remover campo antigo
  }

  // 2. Consolidar campos de telefone
  if (data.phone && !data.telefone) {
    updates.telefone = data.phone;
    updates.phone = null;
  }
  if (data.celular && !data.telefone) {
    updates.telefone = data.celular;
    updates.celular = null;
  }

  // 3. Consolidar campos de CPF
  if (data.documento && !data.cpf) {
    updates.cpf = data.documento;
    updates.documento = null;
  }

  // 4. Consolidar data de nascimento
  if (data.birthDate && !data.dataNascimento) {
    updates.dataNascimento = data.birthDate;
  }
  if (data.data_x0020_de_x0020_nascimento && !data.dataNascimento) {
    updates.dataNascimento = data.data_x0020_de_x0020_nascimento;
  }

  // 5. Garantir estrutura financeira completa
  if (!data.financeiro) {
    updates.financeiro = {
      ativo: true,
      temPacote: false,
      precoSessaoIndividual: 150,
      faturas: [],
      agendamentos: [],
      controleFaltas: {
        totalFaltas: 0,
        faltasNaoJustificadas: 0,
        faltasJustificadas: 0,
        historicoFaltas: [],
      },
    };
  } else {
    // Garantir campos obrigatórios na estrutura financeira
    const financeiro = { ...data.financeiro };
    let financialNeedsUpdate = false;

    if (financeiro.ativo === undefined) {
      financeiro.ativo = true;
      financialNeedsUpdate = true;
    }

    if (financeiro.temPacote === undefined) {
      financeiro.temPacote = false;
      financialNeedsUpdate = true;
    }

    if (financeiro.precoSessaoIndividual === undefined) {
      financeiro.precoSessaoIndividual = 150;
      financialNeedsUpdate = true;
    }

    if (!Array.isArray(financeiro.faturas)) {
      financeiro.faturas = [];
      financialNeedsUpdate = true;
    }

    if (!Array.isArray(financeiro.agendamentos)) {
      financeiro.agendamentos = [];
      financialNeedsUpdate = true;
    }

    if (!financeiro.controleFaltas) {
      financeiro.controleFaltas = {
        totalFaltas: 0,
        faltasNaoJustificadas: 0,
        faltasJustificadas: 0,
        historicoFaltas: [],
      };
      financialNeedsUpdate = true;
    }

    if (financialNeedsUpdate) {
      updates.financeiro = financeiro;
    }
  }

  // 6. Adicionar timestamp de atualização se houve mudanças
  if (Object.keys(updates).length > 0) {
    updates.updatedAt = new Date().toISOString();
    updates.normalizedAt = new Date().toISOString();
  }

  return updates;
}

/**
 * Corrige um paciente específico
 */
export async function normalizePatient(patientId: string): Promise<boolean> {
  try {
    const { data: patient, error: fetchError } = await supabase
      .from("patients")
      .select("*")
      .eq("patientId", patientId)
      .single();

    if (fetchError || !patient) {
      throw new Error(`Paciente ${patientId} não encontrado ou erro ao buscar: ${fetchError?.message}`);
    }

    const normalizedData = normalizePatientDocument(patient);

    if (Object.keys(normalizedData).length > 0) {
      const { error: updateError } = await supabase
        .from("patients")
        .update(normalizedData)
        .eq("patientId", patientId);

      if (updateError) {
        throw updateError;
      }
      console.log(`✅ Paciente ${patientId} normalizado com sucesso`);
      return true;
    }

    console.log(`ℹ️ Paciente ${patientId} já está normalizado`);
    return true;
  } catch (error: any) {
    console.error(`❌ Erro ao normalizar paciente ${patientId}:`, error);
    return false;
  }
}

/**
 * Exporta relatório de auditoria em formato legível
 */
export function formatAuditReport(report: AuditReport): string {
  let output = '═══════════════════════════════════════════════════════\n';
  output += '         RELATÓRIO DE AUDITORIA DE DADOS\n';
  output += '═══════════════════════════════════════════════════════\n\n';
  output += `Data: ${new Date(report.timestamp).toLocaleString('pt-BR')}\n\n`;

  output += '📊 RESUMO GERAL\n';
  output += '─────────────────────────────────────────────────────\n';
  output += `Total de Pacientes: ${report.totalPatients}\n`;
  output += `✅ Pacientes Saudáveis: ${report.patientsHealthy} (${((report.patientsHealthy / report.totalPatients) * 100).toFixed(1)}%)\n`;
  output += `⚠️  Pacientes com Problemas: ${report.patientsWithIssues} (${((report.patientsWithIssues / report.totalPatients) * 100).toFixed(1)}%)\n\n`;

  output += '📈 PROBLEMAS POR TIPO\n';
  output += '─────────────────────────────────────────────────────\n';
  output += `Campos Faltando: ${report.issuesByType.missingFields}\n`;
  output += `Campos Inconsistentes: ${report.issuesByType.inconsistentFields}\n`;
  output += `Campos Vazios: ${report.issuesByType.emptyFields}\n`;
  output += `Sem Dados Financeiros: ${report.issuesByType.noFinancialData}\n\n`;

  output += '📅 AUDITORIA DE AGENDAMENTOS\n';
  output += '─────────────────────────────────────────────────────\n';
  output += `Total de eventos: ${report.appointmentAudit.totalEvents}\n`;
  output += `Com patientId: ${report.appointmentAudit.withPatientId}\n`;
  output += `Sem patientId: ${report.appointmentAudit.withoutPatientId}\n`;
  output += `patientId desconhecido: ${report.appointmentAudit.unknownPatientId}\n`;
  output += `Eventos sem data: ${report.appointmentAudit.missingStart}\n`;
  output += `Eventos sem status: ${report.appointmentAudit.missingStatus}\n`;
  output += `Eventos sem local: ${report.appointmentAudit.missingLocation}\n`;
  output += `Eventos com local inválido: ${report.appointmentAudit.invalidLocation}\n\n`;

  if (report.appointmentAudit.issues.length > 0) {
    output += '⚠️  EVENTOS COM PROBLEMAS\n';
    output += '─────────────────────────────────────────────────────\n';
    report.appointmentAudit.issues.forEach(item => {
      output += `\nEvento: ${item.appointmentId}\n`;
      if (item.patientId) {
        output += `  Paciente vinculado: ${item.patientId}\n`;
      }
      if (item.patientName) {
        output += `  Nome informado: ${item.patientName}\n`;
      }
      if (item.startDate) {
        output += `  Data inicial: ${new Date(item.startDate).toLocaleString('pt-BR')}\n`;
      }
      if (item.appointmentLocation != null) {
        output += `  Local atual: ${item.appointmentLocation}\n`;
      }
      output += `  Campos pendentes: ${item.missingFields.join(', ')}\n`;
    });
    output += '\n';
  }

  if (report.issues.length > 0) {
    output += '⚠️  DETALHES DOS PROBLEMAS\n';
    output += '─────────────────────────────────────────────────────\n\n';

    // Agrupar por severidade
    const critical = report.issues.filter(i => i.severity === 'critical');
    const warning = report.issues.filter(i => i.severity === 'warning');
    const info = report.issues.filter(i => i.severity === 'info');

    if (critical.length > 0) {
      output += `🔴 CRÍTICOS (${critical.length}):\n`;
      critical.forEach(issue => {
        output += formatIssue(issue);
      });
      output += '\n';
    }

    if (warning.length > 0) {
      output += `🟡 AVISOS (${warning.length}):\n`;
      warning.forEach(issue => {
        output += formatIssue(issue);
      });
      output += '\n';
    }

    if (info.length > 0) {
      output += `ℹ️  INFORMATIVOS (${info.length}):\n`;
      info.forEach(issue => {
        output += formatIssue(issue);
      });
    }
  }

  output += '═══════════════════════════════════════════════════════\n';
  return output;
}

function formatIssue(issue: PatientDataIssue): string {
  let output = `\nPaciente: ${issue.patientName} (ID: ${issue.patientId.substring(0, 8)}...)\n`;

  if (issue.missingFields.length > 0) {
    output += `  ❌ Campos Faltando: ${issue.missingFields.join(', ')}\n`;
  }

  if (issue.inconsistentFields.length > 0) {
    output += `  ⚠️  Inconsistências: ${issue.inconsistentFields.join(', ')}\n`;
  }

  if (issue.emptyFields.length > 0) {
    output += `  📝 Campos Vazios: ${issue.emptyFields.join(', ')}\n`;
  }

  output += `  💰 Estrutura Financeira: ${issue.financialStructure}\n`;

  return output;
}
